import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
    EventEntity,
    EventStateEnum,
    EventStateEnumNameMap,
    ITaskPriority,
    taskEventStates,
} from '../../../dave-data-module/entities/event.entity';
import { State } from '../../../dave-data-module/State';
import { EventsActionTypes } from '../../../dave-data-module/State/actions/events.actions';
import { getEventById, getTasks, getTasksFetched } from '../../../dave-data-module/State/selectors/events.selectors';
import { getUser } from '../../../dave-data-module/State/selectors/users.selectors';
import {
    DetailTasksComponent,
    DetailTasksComponentDialogData,
    DetailTasksComponentDialogDataInitialValues
} from "../../../dave-event-card/components/detail-tasks/detail-tasks.component";
import { BreakpointObserverService } from '../../../dave-utils-module/dave-shared-components-module/services/breakpoint-observer.service';
import { compareArrays, isNotNullOrUndefined } from '../../../helper/helper';
import {
    getUserToCommissionCommissionIdsMap, getUserToCommissionDictionary, getUserToCommissionFetched,
} from '../../../dave-data-module/State/selectors/user-to-commission.selector';
import { getFetched$ } from '../../../dave-data-module/helper/helper';
import { UserToCommissionResolver } from '../../../dave-data-module/guards/user-to-commission.resolver';
import { selectEventCategories } from '../../../dave-data-module/State/selectors/event-Categories.selector';
import { filterEventsByCategories } from '../../../helper/event.helper';
import { EventDataService } from '../../../dave-data-module/services/event.service-data-service';
import { EventResolver } from '../../../dave-data-module/guards/event.resolver';

const PAGE_SIZE = 20;

export interface TaskBoardFilter {
    CommissionIds?: number[];
    UserId?: number[];
    AutorId?: number[];
    State?: EventStateEnum[];
    MaxEventEndDate?: Date;
    MinEventEndDate?: Date;
    Priority?: ITaskPriority[];
    MilestoneIds?: number[];
    CategoryIds?: number[];
}

interface lane {
    Headline: string;
    Id: EventStateEnum;
    EventIds: number[];
}
export interface ITaskBoardSort {
    sortBy: 'endDate' | 'priority';
    direction: 'asc' | 'dsc';
}
@Component({
    selector: 'app-tasks-board[FileClicked]',
    templateUrl: './tasks-board.component.html',
    styleUrls: ['./tasks-board.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TasksBoardComponent implements OnInit, OnDestroy {
    @Input() ShowNewButton = true;
    @Input() NewTaskInitialValues: DetailTasksComponentDialogDataInitialValues;
    @Input()
    set Search(search: string) {
        this.search$.next(search);
    }
    @Input()
    set Filter(filter: TaskBoardFilter) {
        this.filter$.next(filter);
    }
    @Input()
    set Sort(sort: ITaskBoardSort) {
        this.sort$.next(sort);
    }
    @Input() PreFilter: {
        authorOrUserToEventOrUserToCommission?: boolean;
        eventIds?: number[];
    };
    @Output() FileClicked = new EventEmitter<number>();

    @Input() showCompleted = false;
    @Input() inlineCompleteToggleButton = false;
    @Input() folderIdForSelectDocumentsDialogInDetailTask: number | undefined;

    private destroy$ = new Subject<void>();

    private search$ = new BehaviorSubject<string>('');
    private filter$ = new BehaviorSubject<TaskBoardFilter>({});
    protected subscriptions: Subscription[] = [];
    public DisableDragNDrop$: Observable<boolean> = combineLatest([
        this.bd.TouchQuery, this.bd.MobileQuery
    ]).pipe(
        map(([isTouch, mobile]) => isTouch && mobile),
    );

    private event2CategoryIds$ = this.store$.select(selectEventCategories).pipe(
        map(ec => ec ? ec.Event2EventCategories : []),
        shareReplay({ bufferSize: 1, refCount: true })
    );
    private onInit$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public sort$: BehaviorSubject<ITaskBoardSort> = new BehaviorSubject({ sortBy: 'endDate', direction: 'dsc' });
    private preFilteredAndSortedTasks$: Observable<EventEntity[]> = this.onInit$.pipe(
        filter((v) => !!v), // hier wird scheinbar auf init gewartet damit this.PreFilter da ist
        switchMap(() => this.filter$.pipe(map(f => f.CommissionIds), distinctUntilChanged(compareArrays))),
        switchMap((commissionIds) => {
            let tasks$: Observable<EventEntity[]>;
            if (commissionIds?.length > 0) {
                tasks$ = this.eventData.GetFromCommissionsTasks$(commissionIds);
            } else if (this.PreFilter?.eventIds) {
                // todo per Ids im BE abrufen
                tasks$ = getFetched$(this.store$, getTasksFetched, getTasks, this.eventResolver).pipe(map(tasks => tasks.filter((t) => this.PreFilter.eventIds.includes(t.Id))));
            } else {
                tasks$ = getFetched$(this.store$, getTasksFetched, getTasks, this.eventResolver);
            }

            if (this.PreFilter && this.PreFilter.authorOrUserToEventOrUserToCommission) {
                return combineLatest([
                    tasks$,
                    getFetched$(this.store$, getUserToCommissionFetched, getUserToCommissionCommissionIdsMap),
                    getFetched$(this.store$, getUserToCommissionFetched, getUserToCommissionDictionary),
                    this.store$.select(getUser),
                ]).pipe(
                    map(([tasks, userToCommissions, userToCommissionDict, user]) =>
                        tasks.filter((t) => t.UserId === user.Id || t.UserIds?.includes(user.Id) || (t.CommissionId && userToCommissions.get(t.CommissionId)?.some(id => userToCommissionDict[id].UserId === user.Id))),
                    ),
                )
            } else {
                return tasks$
            }
        }),
        map((tasks) => tasks.sort((a, b) => {
                const aDate = a.EventEndDate;
                const bDate = b.EventEndDate;
                if (aDate == null && bDate == null) {
                    return 0;
                }
                if (aDate == null && bDate != null) {
                    return 1;
                }
                if (aDate != null && bDate == null) {
                    return -1;
                }
                return aDate.getTime() - bDate.getTime();
            }),
        ),
        switchMap((tasks) =>
            combineLatest([this.filter$, this.event2CategoryIds$]).pipe(
                map(([filter, e2cs]) => {
                    let filteredTasks = tasks.slice();
                    if (filter?.Priority?.length) {
                        filteredTasks = filteredTasks.filter((e) => isNotNullOrUndefined(e.AdditionalData?.Priority) && filter.Priority.includes(e.AdditionalData.Priority));
                    }
                    if (filter?.State) {
                        filteredTasks = filteredTasks.filter((e) => filter.State.includes(e.State));
                    }
                    if (filter?.CommissionIds?.length) {
                        filteredTasks = filteredTasks.filter((e) => filter.CommissionIds.includes(e.CommissionId));
                    }
                    if (filter?.MilestoneIds?.length) {
                        filteredTasks = filteredTasks.filter((e) => filter.MilestoneIds.some(mId => e.MilestoneIds.includes(mId)));
                    }
                    if (filter?.UserId?.length) {
                        filteredTasks = filteredTasks.filter((e) => filter.UserId.some(fid => e.UserIds?.includes(fid)));
                    }
                    if (filter?.AutorId?.length) {
                        filteredTasks = filteredTasks.filter((e) => e.UserId && filter.AutorId.includes(e.UserId));
                    }
                    if (filter?.MaxEventEndDate) {
                        filteredTasks = filteredTasks.filter((e) => e.EventEndDate && filter.MaxEventEndDate.getTime() > e.EventEndDate.getTime());
                    }
                    if (filter?.MinEventEndDate) {
                        filteredTasks = filteredTasks.filter((e) => e.EventEndDate && filter.MinEventEndDate.getTime() < e.EventEndDate.getTime());
                    }
                    if (filter?.CategoryIds?.length) {
                        filteredTasks = filterEventsByCategories(filteredTasks, e2cs, filter.CategoryIds);
                    }
                    return filteredTasks;
                }),
            )
        ),
        switchMap((tasks) =>
            this.sort$.pipe(
                distinctUntilChanged((a, b) => a.sortBy === b.sortBy && a.direction === b.direction),
                map((sort) => {
                    switch (sort.sortBy) {
                        case 'endDate':
                            return sort.direction === 'dsc' ? tasks : tasks.slice().reverse();
                        case 'priority':
                            const sortTask = tasks.slice().sort((a, b) => {
                                if (a.AdditionalData?.Priority === b.AdditionalData?.Priority) {
                                    return 0;
                                }
                                if (!a.AdditionalData?.Priority) {
                                    return -1;
                                }
                                if (!b.AdditionalData?.Priority) {
                                    return 1;
                                }
                                return a.AdditionalData.Priority - b.AdditionalData.Priority;
                            });
                            return sort.direction === 'asc' ? sortTask : sortTask.reverse();
                    }
                }),
            ),
        ),
    ).pipe(
        switchMap((tasks) =>
            this.search$.pipe(
                map((value) => {
                    const searchTerm = (value || '').trim().toLowerCase();
                    return searchTerm;
                }),
                map((searchTerm) => {
                    const filteredTasks = searchTerm
                        ? tasks.filter((data) => {
                            const values = ['', data?.Hint || '', data?.Description || '', data?.Name || '']
                                .map((value) => value?.trim().toLowerCase());
                            return values.some((value) => value?.includes(searchTerm));
                        })
                        : tasks;
                    return filteredTasks;
                }),
            ),
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
    );
    public dataResolved$ = this.preFilteredAndSortedTasks$.pipe(
        map(() => true),
    );
    public NewSwimLanes: {
        Headline: string;
        Id: EventStateEnum;
        events$: Observable<EventEntity[]>;
        page$: BehaviorSubject<number>;
        isLastPage$: Observable<boolean>;
    }[] = Object.values(taskEventStates).map((state) => {
        const page$ = new BehaviorSubject(1);
        const events$ = this.preFilteredAndSortedTasks$.pipe(
            switchMap((tasks) =>
                this.search$.pipe(
                    map((value) => {
                        const searchTerm = (value || '').trim().toLowerCase();
                        return searchTerm;
                    }),

                    map((searchTerm) => {
                        const filteredTasks = searchTerm
                            ? tasks.filter((data) => {
                                const values = ['', data?.Hint || '', data?.Description || '', data?.Name || '']
                                    .map((value) => value?.trim().toLowerCase());
                                return values.some((value) => value?.includes(searchTerm));
                            })
                            : tasks;
                        return filteredTasks;
                    }),
                    map((filteredTasks) =>
                        filteredTasks.filter((task) => task.State === state)
                    )
                )
            ),
            shareReplay({ bufferSize: 1, refCount: true }),
        );


        return {
        Headline: EventStateEnumNameMap.get(state),
        Id: state,
        page$,
        isLastPage$: combineLatest([page$, events$]).pipe(
            map(([page, eventIds]) => page >= (eventIds.length / PAGE_SIZE)),
        ),
        events$: events$.pipe(
            switchMap(events => {
            const maxPageSize = Math.ceil(events.length / PAGE_SIZE);

            page$.next(1);
            return page$.pipe(
                map(p => p > maxPageSize ? maxPageSize : p),
                map(p => events.slice(0, p * PAGE_SIZE)),
                distinctUntilChanged(),
            )}),
        ),
    }});

    constructor(private store$: Store<State>, private dialog: MatDialog, private bd: BreakpointObserverService, u2cResolver: UserToCommissionResolver, private eventData: EventDataService, private eventResolver: EventResolver) {
        firstValueFrom(this.store$.select(getUserToCommissionFetched)).then(f => {
            if (!f) {
                u2cResolver.resolve();
            }
        })
    }
    ngOnInit(): void {
        this.onInit$.next(true);
   }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
        this.destroy$.next();
        this.destroy$.complete();
    }

    nextPage(page$: BehaviorSubject<number>, pages = 1) {
        page$.next(page$.value+pages);
    }

    nextPageAllLanes(pages = 1) {
        this.NewSwimLanes.forEach(({page$}) => this.nextPage(page$, pages));
    }

    toggleCompleted() {
        this.showCompleted = !this.showCompleted;
    }


    Drop(event: CdkDragDrop<EventStateEnum, EventStateEnum, number>) {
        if (event.previousContainer !== event.container) {
            const id = event.item.data;
            const state = event.container.data;
            firstValueFrom(this.store$.select(getEventById({ id }))).then((e) => {
                this.store$.dispatch(EventsActionTypes.UpdateEvent({ Payload: e.Clone({ State: event.container.data }) }));
            });
            this.store$.dispatch(EventsActionTypes.ModifyEvent({ Payload: { id, state } }));
        }
    }
   async OpenAddTaskDialog() {
        const user = await firstValueFrom(this.store$.select(getUser));
        this.dialog.open<DetailTasksComponent, DetailTasksComponentDialogData>(DetailTasksComponent, {
            ...DetailTasksComponent.DefaultConfig,
            data: {
                EventId: null,
                FolderIdForSelectDocumentsDialog: this.folderIdForSelectDocumentsDialogInDetailTask,
                InitialValues: {
                    CommissionId: this.filter$.value?.CommissionIds?.length === 1 ? this.filter$.value?.CommissionIds[0] : null,
                    ...(this.NewTaskInitialValues || {}),
                    UserIds: [user.Id]
                },
            },
        });
    }

    TrackLanes(index: number, lane: { Id: EventStateEnum }) {
        return lane.Id;
    }
}
